import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { GoogleStartRequest } from '@nucleus-commerce/api-client/platform';
import { Button } from '@nucleus-commerce/ui/components/button';

import { ServerError } from '@/shared/ui/server-error';

import { useGoogleStart } from '../hooks/use-google-start';
import { isGoogleUnavailable } from '../schemas/google-rules';

function GoogleMark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className="size-4" fill="currentColor">
      <path d="M21.35 11.1H12v2.98h5.35c-.23 1.4-1.64 4.1-5.35 4.1a5.9 5.9 0 0 1 0-11.8c1.8 0 3.02.77 3.71 1.43l2.53-2.44C16.62 3.9 14.5 3 12 3a9 9 0 1 0 0 18c5.2 0 8.64-3.65 8.64-8.8 0-.59-.06-1.04-.14-1.49z" />
    </svg>
  );
}

export interface GoogleSignInButtonProps {
  getRequest: () => GoogleStartRequest;
}

/**
 * Sends the browser to Google, which returns it to /auth/google/callback. When the platform has
 * no Google client the start answers 409, and the button gives way to a short note.
 */
export function GoogleSignInButton({ getRequest }: GoogleSignInButtonProps) {
  const { t } = useTranslation('auth');
  const start = useGoogleStart();
  const [unavailable, setUnavailable] = useState(false);

  if (unavailable) {
    return (
      <p role="status" className="text-muted-foreground text-center text-xs">
        {t('google.unavailable')}
      </p>
    );
  }

  const leaving = start.isPending || start.isSuccess;
  return (
    <>
      {start.isError ? <ServerError error={start.error} /> : null}
      <Button
        type="button"
        variant="outline"
        size="lg"
        className="w-full"
        disabled={leaving}
        onClick={() =>
          start.mutate(getRequest(), {
            onError: (error) => {
              if (isGoogleUnavailable(error)) setUnavailable(true);
            },
          })
        }
      >
        <GoogleMark />
        {leaving ? t('google.redirecting') : t('google.continue')}
      </Button>
    </>
  );
}
